import { AbortError, StatusError } from '../components/Providers/ErrorProvider';
import { errorMap } from '../Types/serverError';

export const API_URL = import.meta.env.VITE_APP_API_HOST;

let setErrorFunction: ((error: StatusError) => Promise<boolean>) | null;
setErrorFunction = null;

export const registerErrorFunction = (fn: (error: StatusError) => Promise<boolean>) => {
  setErrorFunction = fn;
};

export const handleError = async (error: string, status: number) => {
  if (setErrorFunction) {
    return setErrorFunction(new StatusError(error, status));
  } else {
    console.error(error);
  }
};

export const enrichedFetch = async (
  url: string,
  options = {} as RequestInit,
  retryTimes = 0,
  responseJson = true,
  signal?: AbortSignal
): Promise<unknown> => {
  const headers = new Headers(options.headers);
  if (localStorage.getItem('token')) {
    headers.set('Authorization', `Bearer ${localStorage.getItem('token')}`);
  }

  if (localStorage.getItem('graphToken')) {
    headers.set('GraphToken', `Bearer ${localStorage.getItem('graphToken')}`);
  }
  headers.set('Timezone', `${Intl.DateTimeFormat().resolvedOptions().timeZone}`);

  if (localStorage.getItem('jwt')) {
    headers.set('jwt', `${localStorage.getItem('jwt')}`);
  }

  options.headers = headers;
  if (signal) {
    options.signal = signal;
  }

  let response = null;
  try {
    response = await fetch(url, { ...options, credentials: 'include', mode: 'cors' });
  } catch (error) {
    if (typeof error === 'string' && error.includes('Abort')) {
      console.warn('Fetch aborted:', error);
      throw new AbortError('Fetch aborted', 0);
    }
    const errorMessage = 'Failed to connect to server';
    handleError(errorMessage, 0);
    throw new StatusError(errorMessage, 0);
  }

  if (response.status === 304) return response.json();

  if (response.status >= 200 && response.status < 300) {
    if (responseJson && response.status !== 204) {
      return response.json();
    }

    // For the status code 204 we cannot read the response as JSON
    // but since this response code means "No Content" we know the response was null
    if (responseJson && response.status === 204) {
      return null;
    }

    return response;
  }

  const responseMessage = (await response.json()).message;

  if (responseMessage.startsWith('Error rescanning project questions')) {
    throw new Error(responseMessage);
  }

  if (response.status in errorMap) {
    if (errorMap[response.status].function !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return errorMap[response.status].function!(retryTimes, url, options);
    } else {
      throw new StatusError(
        responseMessage || errorMap[response.status].default_message,
        response.status
      );
    }
  } else {
    throw new StatusError(responseMessage || 'An unknown error occurred', 513);
  }
};
